/**
 * Quadric error simplification (Garland and Heckbert): repeatedly collapses the edge whose removal changes the
 * surface least, until `target` vertices remain. Flat areas lose vertices first; creases, lids, lips and nostrils
 * keep theirs. Collapses that would fold a triangle over or pinch the surface into a non-manifold are refused, and
 * vertices on open edges stay where they are. No edge grows longer than `longest`, so flat areas keep an even mesh that
 * still bends smoothly when the surface is reshaped (face shapes are warps of one mesh).
 */
export function simplifyMesh(positions: Float32Array, indices: Uint32Array, target: number, longest = Infinity): { positions: Float32Array; indices: Uint32Array } {
  const vertexCount = positions.length / 3, triangleCount = indices.length / 3;
  if (vertexCount <= target) return { positions, indices };
  const p = Float64Array.from(positions), tri = Int32Array.from(indices), alive = new Uint8Array(triangleCount).fill(1);
  const quadric = new Float64Array(vertexCount * 10), version = new Uint32Array(vertexCount), dead = new Uint8Array(vertexCount);
  const around: number[][] = Array.from({ length: vertexCount }, () => []);
  for (let t = 0; t < triangleCount; t++) for (let c = 0; c < 3; c++) around[tri[t * 3 + c]].push(t);
  const addPlane = (q: Float64Array, offset: number, a: number, b: number, c: number, d: number, w: number) => {
    q[offset] += w * a * a; q[offset + 1] += w * a * b; q[offset + 2] += w * a * c; q[offset + 3] += w * a * d;
    q[offset + 4] += w * b * b; q[offset + 5] += w * b * c; q[offset + 6] += w * b * d;
    q[offset + 7] += w * c * c; q[offset + 8] += w * c * d; q[offset + 9] += w * d * d;
  };
  const normalOf = (a: number, b: number, c: number, out: number[]) => {
    const ux = p[b * 3] - p[a * 3], uy = p[b * 3 + 1] - p[a * 3 + 1], uz = p[b * 3 + 2] - p[a * 3 + 2];
    const vx = p[c * 3] - p[a * 3], vy = p[c * 3 + 1] - p[a * 3 + 1], vz = p[c * 3 + 2] - p[a * 3 + 2];
    out[0] = uy * vz - uz * vy; out[1] = uz * vx - ux * vz; out[2] = ux * vy - uy * vx;
    return Math.sqrt(out[0] * out[0] + out[1] * out[1] + out[2] * out[2]);
  };
  const n = [0, 0, 0];
  for (let t = 0; t < triangleCount; t++) {
    const a = tri[t * 3], b = tri[t * 3 + 1], c = tri[t * 3 + 2], area2 = normalOf(a, b, c, n);
    if (area2 < 1e-18) continue;
    const nx = n[0] / area2, ny = n[1] / area2, nz = n[2] / area2, d = -(nx * p[a * 3] + ny * p[a * 3 + 1] + nz * p[a * 3 + 2]);
    for (const v of [a, b, c]) addPlane(quadric, v * 10, nx, ny, nz, d, area2 / 2);
  }
  // Open edges belong to one triangle; their vertices are pinned.
  const pinned = new Uint8Array(vertexCount), edgeUses = new Map<number, number>();
  const key = (a: number, b: number) => a < b ? a * vertexCount + b : b * vertexCount + a;
  for (let t = 0; t < triangleCount; t++) for (let c = 0; c < 3; c++) { const k = key(tri[t * 3 + c], tri[t * 3 + (c + 1) % 3]); edgeUses.set(k, (edgeUses.get(k) ?? 0) + 1); }
  for (const [k, uses] of edgeUses) if (uses !== 2) { pinned[Math.floor(k / vertexCount)] = 1; pinned[k % vertexCount] = 1; }

  const error = (q: Float64Array, x: number, y: number, z: number) =>
    q[0] * x * x + 2 * q[1] * x * y + 2 * q[2] * x * z + 2 * q[3] * x + q[4] * y * y + 2 * q[5] * y * z + 2 * q[6] * y + q[7] * z * z + 2 * q[8] * z + q[9];
  const sum = new Float64Array(10);
  /** The best place for the merged vertex: the quadric's minimum when it is well defined, else the better end or the middle. */
  const placement = (a: number, b: number, out: number[]) => {
    for (let i = 0; i < 10; i++) sum[i] = quadric[a * 10 + i] + quadric[b * 10 + i];
    const [q0, q1, q2, q3, q4, q5, q6, q7, q8] = sum;
    const det = q0 * (q4 * q7 - q5 * q5) - q1 * (q1 * q7 - q5 * q2) + q2 * (q1 * q5 - q4 * q2);
    const candidates: number[][] = [];
    if (Math.abs(det) > 1e-15) {
      const x = -(q3 * (q4 * q7 - q5 * q5) - q1 * (q6 * q7 - q5 * q8) + q2 * (q6 * q5 - q4 * q8)) / det;
      const y = -(q0 * (q6 * q7 - q8 * q5) - q3 * (q1 * q7 - q5 * q2) + q2 * (q1 * q8 - q6 * q2)) / det;
      const z = -(q0 * (q4 * q8 - q5 * q6) - q1 * (q1 * q8 - q6 * q2) + q3 * (q1 * q5 - q4 * q2)) / det;
      // Only trust the minimum near the edge itself.
      const mx = (p[a * 3] + p[b * 3]) / 2, my = (p[a * 3 + 1] + p[b * 3 + 1]) / 2, mz = (p[a * 3 + 2] + p[b * 3 + 2]) / 2;
      const length2 = (p[a * 3] - p[b * 3]) ** 2 + (p[a * 3 + 1] - p[b * 3 + 1]) ** 2 + (p[a * 3 + 2] - p[b * 3 + 2]) ** 2;
      if ((x - mx) ** 2 + (y - my) ** 2 + (z - mz) ** 2 < length2) candidates.push([x, y, z]);
    }
    candidates.push([p[a * 3], p[a * 3 + 1], p[a * 3 + 2]], [p[b * 3], p[b * 3 + 1], p[b * 3 + 2]], [(p[a * 3] + p[b * 3]) / 2, (p[a * 3 + 1] + p[b * 3 + 1]) / 2, (p[a * 3 + 2] + p[b * 3 + 2]) / 2]);
    let best = Infinity;
    for (const [x, y, z] of candidates) {
      // A pinned vertex cannot move.
      if (pinned[a] && (x !== p[a * 3] || y !== p[a * 3 + 1] || z !== p[a * 3 + 2])) continue;
      if (pinned[b] && (x !== p[b * 3] || y !== p[b * 3 + 1] || z !== p[b * 3 + 2])) continue;
      const cost = error(sum, x, y, z);
      if (cost < best) { best = cost; out[0] = x; out[1] = y; out[2] = z; }
    }
    return best;
  };

  // A binary heap of candidate collapses, keyed by cost; stale entries are skipped when popped.
  const heapCost: number[] = [], heapA: number[] = [], heapB: number[] = [], heapVa: number[] = [], heapVb: number[] = [];
  const swap = (i: number, j: number) => {
    [heapCost[i], heapCost[j]] = [heapCost[j], heapCost[i]]; [heapA[i], heapA[j]] = [heapA[j], heapA[i]]; [heapB[i], heapB[j]] = [heapB[j], heapB[i]];
    [heapVa[i], heapVa[j]] = [heapVa[j], heapVa[i]]; [heapVb[i], heapVb[j]] = [heapVb[j], heapVb[i]];
  };
  const push = (a: number, b: number) => {
    const at = [0, 0, 0], cost = placement(a, b, at);
    if (cost === Infinity) return;
    let i = heapCost.length;
    heapCost.push(cost); heapA.push(a); heapB.push(b); heapVa.push(version[a]); heapVb.push(version[b]);
    while (i > 0) { const parent = (i - 1) >> 1; if (heapCost[parent] <= heapCost[i]) break; swap(i, parent); i = parent; }
  };
  const pop = () => {
    const last = heapCost.length - 1;
    swap(0, last);
    const entry = [heapA[last], heapB[last], heapVa[last], heapVb[last]];
    heapCost.pop(); heapA.pop(); heapB.pop(); heapVa.pop(); heapVb.pop();
    let i = 0;
    for (;;) {
      const l = i * 2 + 1, r = l + 1;
      let m = i;
      if (l < heapCost.length && heapCost[l] < heapCost[m]) m = l;
      if (r < heapCost.length && heapCost[r] < heapCost[m]) m = r;
      if (m === i) break;
      swap(i, m); i = m;
    }
    return entry;
  };
  const neighbours = (v: number) => {
    const set = new Set<number>();
    for (const t of around[v]) if (alive[t]) for (let c = 0; c < 3; c++) if (tri[t * 3 + c] !== v) set.add(tri[t * 3 + c]);
    return set;
  };
  for (let v = 0; v < vertexCount; v++) for (const w of neighbours(v)) if (v < w) push(v, w);

  let remaining = vertexCount;
  const at = [0, 0, 0], before = [0, 0, 0], after = [0, 0, 0];
  while (remaining > target && heapCost.length) {
    const [a, b, va, vb] = pop();
    if (dead[a] || dead[b] || version[a] !== va || version[b] !== vb) continue;
    // Two triangles share an inner edge, and the ends may share no other neighbour, or the surface would pinch.
    const na = neighbours(a), nb = neighbours(b);
    let shared = 0;
    for (const v of na) if (nb.has(v)) shared++;
    if (shared !== 2) continue;
    placement(a, b, at);
    let long = false;
    for (const v of [na, nb]) for (const w of v) if (w !== a && w !== b && (p[w * 3] - at[0]) ** 2 + (p[w * 3 + 1] - at[1]) ** 2 + (p[w * 3 + 2] - at[2]) ** 2 > longest * longest) long = true;
    if (long) continue;
    // Refuse collapses that turn a triangle over or squash it flat.
    let folds = false;
    for (const v of [a, b]) for (const t of around[v]) {
      if (!alive[t]) continue;
      const i0 = tri[t * 3], i1 = tri[t * 3 + 1], i2 = tri[t * 3 + 2];
      if ((i0 === a || i1 === a || i2 === a) && (i0 === b || i1 === b || i2 === b)) continue;
      const area = normalOf(i0, i1, i2, before);
      const keep = [p[v * 3], p[v * 3 + 1], p[v * 3 + 2]];
      p[v * 3] = at[0]; p[v * 3 + 1] = at[1]; p[v * 3 + 2] = at[2];
      const moved = normalOf(i0, i1, i2, after);
      p[v * 3] = keep[0]; p[v * 3 + 1] = keep[1]; p[v * 3 + 2] = keep[2];
      if (moved < area * .02 || before[0] * after[0] + before[1] * after[1] + before[2] * after[2] < .3 * area * moved) { folds = true; break; }
    }
    if (folds) continue;
    // Merge b into a.
    p[a * 3] = at[0]; p[a * 3 + 1] = at[1]; p[a * 3 + 2] = at[2];
    for (let i = 0; i < 10; i++) quadric[a * 10 + i] += quadric[b * 10 + i];
    pinned[a] = pinned[a] || pinned[b];
    for (const t of around[b]) {
      if (!alive[t]) continue;
      const hasA = tri[t * 3] === a || tri[t * 3 + 1] === a || tri[t * 3 + 2] === a;
      if (hasA) { alive[t] = 0; continue; }
      for (let c = 0; c < 3; c++) if (tri[t * 3 + c] === b) tri[t * 3 + c] = a;
      around[a].push(t);
    }
    around[a] = around[a].filter(t => alive[t]);
    // Only the merged vertex's edges change cost; its old entries go stale with its version.
    dead[b] = 1; remaining--; version[a]++;
    for (const w of neighbours(a)) push(a, w);
  }
  const remap = new Int32Array(vertexCount).fill(-1), outPositions: number[] = [], outIndices: number[] = [];
  for (let t = 0; t < triangleCount; t++) {
    if (!alive[t]) continue;
    for (let c = 0; c < 3; c++) {
      const v = tri[t * 3 + c];
      if (remap[v] < 0) { remap[v] = outPositions.length / 3; outPositions.push(p[v * 3], p[v * 3 + 1], p[v * 3 + 2]); }
      outIndices.push(remap[v]);
    }
  }
  return { positions: new Float32Array(outPositions), indices: new Uint32Array(outIndices) };
}
